import { DrawRequestSchema, DrawResponseDTOSchema, type DrawRequestDTO, type DrawResponseDTO } from '@lilith-arcana-os/domain';

export class EntropyNetworkError extends Error {
  readonly rootCause?: unknown;

  public constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'EntropyNetworkError';
    this.rootCause = cause;
  }
}

export class EntropyValidationError extends Error {
  readonly rootCause?: unknown;

  public constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'EntropyValidationError';
    this.rootCause = cause;
  }
}

export class EntropyPermissionError extends Error {
  public constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'EntropyPermissionError';
  }
}

const normalizeRestBase = (restBase: string): string => restBase.replace(/\/+$/, '');

export class ServerEntropyClient {
  readonly #restBase: string;
  readonly #nonce: string;

  public constructor(input: { restBase: string; nonce: string }) {
    this.#restBase = normalizeRestBase(input.restBase);
    this.#nonce = input.nonce;
  }

  public async draw(request: DrawRequestDTO): Promise<DrawResponseDTO> {
    let payload: DrawRequestDTO;

    try {
      payload = DrawRequestSchema.parse(request);
    } catch (error) {
      throw new EntropyValidationError('Hosted draw request did not match the contract kernel.', error);
    }

    let response: Response;

    try {
      response = await fetch(`${this.#restBase}/draw`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': this.#nonce,
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      throw new EntropyNetworkError('Unable to reach hosted entropy service.', error);
    }

    if (response.status === 401 || response.status === 403) {
      throw new EntropyPermissionError('Hosted draw requires an authenticated WordPress session.', response.status);
    }

    if (!response.ok) {
      const message = await response.text();
      throw new EntropyValidationError(message || 'Hosted draw rejected the request.');
    }

    let rawJson: unknown;

    try {
      rawJson = (await response.json()) as unknown;
    } catch (error) {
      throw new EntropyValidationError('Hosted draw response was not valid JSON.', error);
    }

    try {
      return DrawResponseDTOSchema.parse(rawJson);
    } catch (error) {
      throw new EntropyValidationError('Hosted draw response did not match the contract kernel.', error);
    }
  }
}
